import React from "react"

// components
import {
  Row,
  Space,
  Typography,
  Button,
  Divider,
  Input,
  message,
  Form,
  Checkbox,
  Segmented,
  ConfigProvider,
} from "antd"
import {
  ShoppingCartOutlined,
  ContactsFilled,
  CarFilled,
  ArrowRightOutlined,
} from "@ant-design/icons"
import UserInfo from "components/UserInfo"

// configs
import config from "config"

// firebase
import { Firestore } from "services/firebase"
import { doc, serverTimestamp, updateDoc } from "firebase/firestore"

// context
import { useAuth } from "store/auth"
import { useTranslation } from "react-i18next"
import { useOrders } from "store/orders"

// custom
import AuthBox from "components/AuthBox"

// utils
import { formatAsCurrency } from "utils/currency"

const OrderDrawer = ({ back, onFinish }) => {
  const orders = useOrders()
  const { t } = useTranslation()
  const { isAuthentificated, user, setUser } = useAuth()
  const [orderForm] = Form.useForm()
  const [state, setState] = React.useState("idle")
  const [messageApi, contextHolder] = message.useMessage()

  return (
    <div>
      {contextHolder}
      <Row justify="start">
        <Button
          type="dashed"
          onClick={() => back()}
          icon={<ShoppingCartOutlined />}
        >
          {t("back")}
        </Button>
      </Row>

      <Divider />

      {isAuthentificated && <UserInfo />}

      {!isAuthentificated && (
        <Typography.Paragraph>{t("please_choose")}</Typography.Paragraph>
      )}

      <Divider />

      {!isAuthentificated ? (
        <AuthBox />
      ) : (
        <Form
          scrollToFirstError
          form={orderForm}
          name="customer"
          layout="vertical"
          initialValues={{
            type: "self-pick-up",
            customer_no: user.data?.customer_no,
            firstname: user.data?.firstname,
            familyname: user.data?.familyname,
            street: user.data?.street,
            zip: user.data?.zip,
            city: user.data?.city,
            tel: user.data?.tel,
            send_email: user.data?.send_email,
            save_data: user.data?.save_data,
          }}
          onFinish={async (values) => {
            try {
              setState("checkout-order")

              if (values.save_data) {
                const update = {}

                if (values.customer_no) update.customer_no = values.customer_no
                if (values.firstname) update.firstname = values.firstname
                if (values.familyname) update.familyname = values.familyname
                if (values.street) update.street = values.street
                if (values.zip) update.zip = values.zip
                if (values.city) update.city = values.city
                if (values.tel) update.tel = values.tel
                if (values.send_email) update.send_email = values.send_email
                if (values.save_data) update.save_data = values.save_data

                await updateDoc(doc(Firestore, "users", user.uid), update)

                setUser((prev) => {
                  const u = { ...prev }
                  u.data = { ...u.data, ...update }
                  return u
                })
              }

              const newOrder = {
                resto_key: config.key,
                created_at: serverTimestamp(),

                customer_no: values.customer_no || null,
                firstname: values.firstname || null,
                familyname: values.familyname || null,
                street: values.street || null,
                zip: values.zip || null,
                city: values.city || null,
                tel: values.tel || null,
                send_email: values.send_email || null,
                save_data: values.save_data || null,

                type: values.type,

                email: user.email || null,

                note: values.note || null,

                items: orders.order.list,
                total: orders.order.total,

                status: "created",

                creator_uid: user.uid,
              }

              messageApi.success(t("order_successful"))
              onFinish(newOrder)
            } catch (e) {
              messageApi.error(e.message)
              console.error(e)
            }
          }}
        >
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: "rgba(0,0,0,0.88)",
              },
              components: {
                Segmented: {
                  itemSelectedBg: "#e6f4ff",
                  trackBg: "#f8f8f8aa",
                  borderRadius: 10,
                  trackPadding: 10,
                  controlHeight: 60,
                  fontSize: 16,
                  lineWidth: 1,
                  boxShadowTertiary:
                    "      0 1px 2px 0 rgba(0, 0, 0, 0.03),      0 1px 6px -1px rgba(0, 0, 0, 0.02),      0 2px 4px 0 rgba(0, 0, 0, 0.2)    ",
                },
              },
            }}
          >
            <Form.Item
              label={
                <Space direction="vertical">
                  <Typography.Text strong>
                    {t("please_choose_colon")}
                  </Typography.Text>
                  <Typography.Text italic>{t("order_for")}</Typography.Text>
                </Space>
              }
              hidden={!config.delivery}
              name="type"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (value === "self-pick-up") {
                      return Promise.resolve()
                    }

                    const matchedMinAMountItem =
                      config.min_delivery_amount_by_zip.find(
                        (item) =>
                          item.zip === (getFieldValue("zip") || "").trim()
                      )

                    return orders.order.total >=
                      (matchedMinAMountItem?.min_delivery_amount ||
                        config.min_delivery_amount)
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            t("min_order_error", {
                              amount:
                                matchedMinAMountItem?.min_delivery_amount ||
                                config.min_delivery_amount,
                            })
                          )
                        )
                  },
                }),
              ]}
            >
              <Segmented
                data-testid="order-type-toggle"
                options={[
                  {
                    label: t("pickup"),
                    value: "self-pick-up",
                    icon: <ContactsFilled />,
                  },
                  {
                    label: t("delivery"),
                    value: "delivery",
                    icon: <CarFilled />,
                    disabled: !config.delivery,
                  },
                ]}
              />
            </Form.Item>
          </ConfigProvider>

          <Form.Item shouldUpdate noStyle>
            {() =>
              orderForm.getFieldValue("type") && (
                <Typography.Title level={3}>
                  {t("order_for")}{" "}
                  {orderForm.getFieldValue("type") === "self-pick-up"
                    ? t("pickup")
                    : t("delivery")}
                  :
                </Typography.Title>
              )
            }
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {() =>
              orderForm.getFieldValue("type") && (
                <>
                  <Typography.Title level={4}>
                    {t("customer_info")}
                  </Typography.Title>

                  <Form.Item label={t("customer_number")} name="customer_no">
                    <Input />
                  </Form.Item>

                  <Form.Item
                    label={t("first_name")}
                    name="firstname"
                    rules={[
                      {
                        required: true,
                        message: t("please_enter_data"),
                      },
                    ]}
                  >
                    <Input autoComplete="given-name" />
                  </Form.Item>

                  <Form.Item
                    label={t("last_name")}
                    name="familyname"
                    rules={[
                      {
                        required: true,
                        message: t("please_enter_data"),
                      },
                    ]}
                  >
                    <Input autoComplete="family-name" />
                  </Form.Item>

                  <Form.Item
                    label={t("phone_number")}
                    name="tel"
                    rules={[
                      ({ getFieldValue }) => ({
                        required: true || getFieldValue("type") === "delivery",
                        message: t("please_enter_data"),
                      }),
                      {
                        pattern: /^[\d\s\-\+\(\)\/\.]+$/,
                        message: t("valid_phone"),
                      },
                    ]}
                  >
                    <Input autoComplete="tel" />
                  </Form.Item>

                  <Form.Item shouldUpdate noStyle>
                    {() =>
                      orderForm.getFieldValue("type") === "delivery" && (
                        <>
                          <Typography.Title level={4}>
                            {t("delivery_address")}
                          </Typography.Title>

                          <Form.Item
                            label={t("street")}
                            name="street"
                            rules={[
                              {
                                required: true,
                                message: t("please_enter_data"),
                              },
                            ]}
                          >
                            <Input autoComplete="address-line1" />
                          </Form.Item>

                          <Form.Item
                            label={t("zip")}
                            name="zip"
                            rules={[
                              {
                                required: true,
                                message: t("please_enter_data"),
                              },
                            ]}
                          >
                            <Input autoComplete="postal-code" />
                          </Form.Item>

                          <Form.Item
                            label={t("city")}
                            name="city"
                            rules={[
                              {
                                required: true,
                                message: t("please_enter_data"),
                              },
                            ]}
                          >
                            <Input autoComplete="address-level2" />
                          </Form.Item>
                        </>
                      )
                    }
                  </Form.Item>

                  <Divider />

                  <Form.Item label={t("order_note_label")} name="note">
                    <Input.TextArea
                      rows={5}
                      placeholder={t("order_note_placeholder")}
                    />
                  </Form.Item>

                  <Form.Item valuePropName="checked" name="save_data">
                    <Checkbox>{t("save_data")} </Checkbox>
                  </Form.Item>

                  <Form.Item shouldUpdate noStyle>
                    {() =>
                      orderForm.getFieldValue("type") === "delivery" &&
                      orders.order.total <=
                        (config.min_delivery_amount_by_zip.find(
                          (item) =>
                            item.zip ===
                            (orderForm.getFieldValue("zip") || "").trim()
                        )?.min_delivery_amount ||
                          config.min_delivery_amount) && (
                        <>
                          <Typography.Paragraph type="danger">
                            <Typography.Text strong type="danger">
                              {t("warning")}
                            </Typography.Text>{" "}
                            {t("below_minimum")}{" "}
                            {formatAsCurrency(
                              config.min_delivery_amount_by_zip.find(
                                (item) =>
                                  item.zip ===
                                  (orderForm.getFieldValue("zip") || "").trim()
                              )?.min_delivery_amount ||
                                config.min_delivery_amount
                            )}{" "}
                            {t("delivery_area_note")}
                          </Typography.Paragraph>

                          <Divider />
                        </>
                      )
                    }
                  </Form.Item>

                  <Form.Item shouldUpdate noStyle>
                    {() => (
                      <Form.Item>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={
                            state === "checkout-order" ? true : undefined
                          }
                        >
                          {t("next")}
                          <ArrowRightOutlined />
                        </Button>
                      </Form.Item>
                    )}
                  </Form.Item>
                </>
              )
            }
          </Form.Item>
        </Form>
      )}

      {!isAuthentificated && (
        <>
          <Divider />

          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            {t("guest_privacy_note")}
          </Typography.Paragraph>
        </>
      )}
    </div>
  )
}

export default OrderDrawer
